import { useEffect, useMemo } from 'react';
import { useGetTickerOptionFlowLazyQuery } from 'types/generated/client';
import calculatePercentOtm from 'utils/shared/trades/calculatePercentOtm';
import getDerivedTradeValues from 'utils/shared/trades/getDerivedTradeValues';
import isUnusual from 'utils/shared/trades/isUnusual';
import isSweep from 'utils/shared/trades/isSweep';
import { RawTrade } from 'utils/shared/trades/types';
import { calculatePutFlow } from 'utils/shared/trades/calculatePutFlow';
import {
  getDefaultFlowSideBasisSelection,
  type FlowSideBasisSelection,
} from 'utils/shared/trades/presets';
import { filterTradesByFlowSideBasis } from 'utils/shared/trades/aggregateOptionFlow';
import { getTradeSidesForFlowSideBasis } from 'utils/shared/trades/flowSideBasis';
import { getTickerFlowSinceDate } from 'utils/shared/trades/tickerFlowWindow';
import { useTickerDailyVolume } from 'hooks/useTickerDailyVolume';

export const useTickerData = (
  ticker: string | undefined,
  isOpen: boolean,
  isWallstreetTimezone: boolean,
  flowSideBasis?: FlowSideBasisSelection,
) => {
  const activeFlowSideBasis = useMemo(
    () => flowSideBasis || getDefaultFlowSideBasisSelection(),
    [flowSideBasis],
  );
  const tradeSides = useMemo(
    () => getTradeSidesForFlowSideBasis(activeFlowSideBasis),
    [activeFlowSideBasis],
  );
  // PERF: literal rolling date bound so the Latest view uses the date index. See
  // utils/shared/trades/tickerFlowWindow.
  const sinceDate = useMemo(() => getTickerFlowSinceDate(), []);
  const [queryForTicker, { data: tickerData, loading }] = useGetTickerOptionFlowLazyQuery({
    variables: {
      ticker: ticker?.toUpperCase() || '',
      tradeSides,
      sinceDate,
    },
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (isOpen && ticker) {
      queryForTicker({
        variables: {
          ticker: ticker.toUpperCase(),
          tradeSides,
          sinceDate,
        },
      });
    }
  }, [isOpen, ticker, tradeSides, sinceDate, queryForTicker]);

  const trades = useMemo(() => {
    const apiTrades: Array<RawTrade> = tickerData?.ticker_option_flow || [];
    const processedTrades = apiTrades
      .map((t) => {
        const spot = t.spot || t.priceExternalApi;
        return {
          ...t,
          spot,
          otmPercent: calculatePercentOtm(t.strike, spot, t.cp || ''),
        };
      })
      .map((trade) => {
        return getDerivedTradeValues(trade, isWallstreetTimezone);
      });

    return filterTradesByFlowSideBasis(processedTrades, activeFlowSideBasis);
  }, [tickerData, isWallstreetTimezone, activeFlowSideBasis]);

  const sweepTrades = useMemo(() => trades.filter((t) => isSweep(t)), [trades]);
  const unusualTrades = useMemo(() => trades.filter((t) => isUnusual(t)), [trades]);
  // Daily volume now comes from the cached, single-flight /daily-volume endpoint
  // instead of being bundled into the (uncached, polled) ticker feed query.
  const { dailyVolume: liveDailyVolume } = useTickerDailyVolume(ticker || '', {
    enabled: isOpen && !!ticker,
  });
  const dailyVolume = liveDailyVolume ?? 0;
  const { putFlow, putFlowPremium, putFlowVolume, putVolume, callVolume, putPremium, callPremium } =
    useMemo(
      () =>
        calculatePutFlow(trades, { flowSideBasis: activeFlowSideBasis, putFlowBasis: 'premium' }),
      [trades, activeFlowSideBasis],
    );

  return {
    trades,
    sweepTrades,
    unusualTrades,
    dailyVolume,
    putFlow,
    putFlowPremium,
    putFlowVolume,
    putVolume,
    callVolume,
    putPremium,
    callPremium,
    loading,
  };
};
